import { Box, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DOWNY_MODEL, MELCAST_MODEL } from "../../config/diseaseRisk.config";
import { formatRiskDate } from "../../utils/diseaseRiskUtils";
import { RiskDailyChart } from "./diseaseRiskCharts.component";

const display = (value) => value === null || value === undefined ? "—" : value;
const Flag = ({ value }) => <Chip size="small" variant="outlined" color={value ? "info" : "default"} label={value === true ? "Attivate" : value === false ? "Non attivate" : "Non disponibili"} />;

const Metric = ({ label, value }) => <Box>
  <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
  <Typography component="p" variant="h6" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>{display(value)}</Typography>
</Box>;

const TechnicalTable = ({ rows, columns, label }) => <TableContainer>
  <Table size="small" aria-label={label}>
    <TableHead><TableRow>{columns.map(([key, title]) => <TableCell key={key} component="th" scope="col">{title}</TableCell>)}</TableRow></TableHead>
    <TableBody>{rows.map((row) => <TableRow key={row.date}>
      {columns.map(([key]) => <TableCell key={key} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {key === "date" ? formatRiskDate(row.date) : key.endsWith("_start") ? <Flag value={row[key]} /> : display(row[key])}
      </TableCell>)}
    </TableRow>)}</TableBody>
  </Table>
</TableContainer>;

const DiseaseRiskDetail = ({ result, selected, model, onSelect }) => {
  const isDowny = model === DOWNY_MODEL;
  const isMelcast = model === MELCAST_MODEL;
  const days = selected.daily_severity || selected.daily_downy || [];
  const firstDay = days[0]?.date;
  const lastDay = days[days.length - 1]?.date;
  return <Stack spacing={2.5} component="section" aria-label="Dettaglio del modello">
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
      <Box>
        <Typography component="h3" fontSize={18} fontWeight={600}>{isMelcast ? "Dettaglio giornaliero" : "Dettaglio della valutazione"}</Typography>
        <Typography variant="body2" color="text.secondary">
          {isMelcast ? `Inizio stagione del modello: ${formatRiskDate(result.metadata?.season_start_date)}` :
            `Dati utilizzati: ${formatRiskDate(firstDay || selected.start_date)} – ${formatRiskDate(lastDay)}`}
        </Typography>
      </Box>
      <TextField select size="small" label={isMelcast ? "Giorno" : "Data della valutazione"} value={selected.date}
        onChange={(event) => onSelect(event.target.value)} sx={{ minWidth: 210 }}>
        {[...result.assessments].reverse().map((item) => <MenuItem value={item.date} key={item.date}>{formatRiskDate(item.date)}</MenuItem>)}
      </TextField>
    </Stack>
    <Stack direction="row" gap={4} flexWrap="wrap" useFlexGap aria-live="polite">
      {isMelcast ? <>
        <Metric label="EFI giornaliero" value={selected.efi} />
        <Metric label="EFI cumulato dalla stagione" value={selected.cumulative_efi} />
        <Metric label="Intervallo EFI" value={result.metadata?.efi_interval} />
        <Metric label="Soglie raggiunte" value={selected.spray_recommendation} />
      </> : isDowny ? <>
        <Metric label="Ore SIP dell’ultimo giorno osservato" value={selected.n_sip_hours} />
        <Metric label="Ore germinazione dell’ultimo giorno osservato" value={selected.n_germination_hours} />
        <Metric label="Ore SIP nella finestra" value={selected.cumulative_sip_hours} />
        <Metric label="Ore germinazione nella finestra" value={selected.cumulative_germination_hours} />
        <Box><Typography variant="caption" display="block" color="text.secondary">Condizioni SIP</Typography><Flag value={selected.sip_start} /></Box>
        <Box><Typography variant="caption" display="block" color="text.secondary">Condizioni germinazione</Typography><Flag value={selected.germination_start} /></Box>
      </> : <>
        <Metric label="Severità cumulata nella finestra" value={selected.cumulative_severity} />
        <Box><Typography variant="caption" display="block" color="text.secondary">Indicazione restituita dal modello</Typography>
          <Typography fontWeight={600}>{selected.action || "Non disponibile"}</Typography>
        </Box>
      </>}
    </Stack>
    {isMelcast ? <>
      <Typography variant="body2" color="text.secondary">Il contatore indica soglie del modello raggiunte, non trattamenti eseguiti.</Typography>
      <TechnicalTable rows={[selected]} label="Condizioni giornaliere MELCAST" columns={[
        ["date", "Data"], ["n_leaf_wetness_hours", "Bagnatura fogliare (h)"], ["mean_temperature_lw", "Temperatura nelle ore bagnate (°C)"],
        ["efi", "EFI"], ["cumulative_efi", "EFI cumulato"], ["spray_recommendation", "Soglie raggiunte"],
      ]} />
    </> : days.length > 0 ? <>
      {!isDowny && <RiskDailyChart data={days} />}
      <TechnicalTable rows={days} label={isDowny ? "Finestra di osservazione peronospora" : "Finestra di osservazione oidio"} columns={isDowny ? [
        ["date", "Data"], ["n_bf_hours", "Bagnatura (h)"], ["n_t_ok_hours", "Temperatura favorevole (h)"], ["n_sip_hours", "Ore SIP"],
        ["sip_start", "Condizioni SIP"], ["n_germination_hours", "Ore germinazione"], ["germination_start", "Condizioni germinazione"],
      ] : [["date", "Data"], ["n_leaf_wetness_hours", "Bagnatura fogliare (h)"], ["severity", "Severità (0–2)"], ["cumulative_severity", "Progressivo nella finestra"]]} />
    </> : <Typography color="text.secondary">Dettaglio della finestra non disponibile.</Typography>}
    <Typography variant="caption" color="text.secondary">Sensore meteo utilizzato: {selected.weather_sensor_id || result.metadata?.weather_sensor_id || "Non disponibile"}</Typography>
  </Stack>;
};

export default DiseaseRiskDetail;
